"use client";

import { useState, useRef, useEffect } from "react";
import { motion, useInView, animate, AnimatePresence } from "framer-motion";
import {
  Download,
  HardHat,
  Wrench,
  Truck,
  Zap,
  Gauge,
  Users,
  Clock,
  ShieldCheck,
  Clock3,
  ShieldX,
  CircleCheck,
  Send,
  FileCheck2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import ScrollReveal from "./ScrollReveal";

const EASE = [0.32, 0.72, 0, 1] as const;

// Mirrors the real product's status semáforo (contractor-checklist.tsx).
const statusStyles: Record<
  "vigente" | "por_vencer" | "vencido",
  { label: string; Icon: LucideIcon; iconTone: string; chip: string }
> = {
  vigente: {
    label: "Vigente",
    Icon: ShieldCheck,
    iconTone: "bg-emerald-50 text-emerald-600",
    chip: "bg-emerald-50 text-emerald-700",
  },
  por_vencer: {
    label: "Por vencer",
    Icon: Clock3,
    iconTone: "bg-amber-50 text-amber-600",
    chip: "bg-amber-50 text-amber-700",
  },
  vencido: {
    label: "Vencido",
    Icon: ShieldX,
    iconTone: "bg-red-50 text-red-500",
    chip: "bg-red-50 text-red-600",
  },
};

function CountUp({ target, inView }: { target: number; inView: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!inView || hasRun.current || !ref.current) return;
    hasRun.current = true;
    const controls = animate(0, target, {
      duration: 1.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => {
        if (ref.current) ref.current.textContent = Math.round(v).toString();
      },
    });
    return () => controls.stop();
  }, [inView, target]);

  return <span ref={ref}>0</span>;
}

// ======================== 1 · CONTROL DOCUMENTAL ========================

const contractors = [
  {
    name: "Constructora Andes",
    Icon: HardHat,
    docs: [
      { type: "Certificado F30-1", status: "vigente" as const },
      { type: "Contrato vigente", status: "vigente" as const },
      { type: "Licencia municipal", status: "por_vencer" as const },
    ],
  },
  {
    name: "Servicios ProClean",
    Icon: Wrench,
    docs: [
      { type: "Póliza de seguro", status: "por_vencer" as const },
      { type: "Certificado F30-1", status: "vigente" as const },
      { type: "Reg. sanitario", status: "vencido" as const },
    ],
  },
  {
    name: "Transportes Rápidos",
    Icon: Truck,
    docs: [
      { type: "Revisión técnica", status: "por_vencer" as const },
      { type: "Permiso circulación", status: "vigente" as const },
      { type: "Póliza de seguro", status: "vigente" as const },
    ],
  },
  {
    name: "Eléctrica del Sur",
    Icon: Zap,
    docs: [
      { type: "Certificado SEC", status: "vigente" as const },
      { type: "Licencia eléctrica", status: "por_vencer" as const },
      { type: "Contrato marco", status: "vencido" as const },
    ],
  },
];

// Visual state per position in the deck: 0 = front, 1–2 = peeking behind,
// 3 = just dealt away (drops below the front card and fades out).
const stackPose = [
  { y: 0, scale: 1, opacity: 1, zIndex: 40 },
  { y: -14, scale: 0.94, opacity: 1, zIndex: 30 },
  { y: -26, scale: 0.88, opacity: 0.55, zIndex: 20 },
  { y: 44, scale: 0.96, opacity: 0, zIndex: 10 },
] as const;

function DocumentStackMockup() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [activeIndex, setActiveIndex] = useState(0);
  const hovered = useRef(false);
  const n = contractors.length;

  useEffect(() => {
    if (!inView) return;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    const timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        if (hovered.current) return;
        setActiveIndex((prev) => (prev + 1) % n);
      }, 2600);
    }, 1500);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId !== undefined) clearInterval(intervalId);
    };
  }, [inView, n]);

  return (
    <div
      ref={ref}
      className="flex h-full items-center justify-center px-6"
      onMouseEnter={() => {
        hovered.current = true;
      }}
      onMouseLeave={() => {
        hovered.current = false;
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
        className="relative h-[186px] w-full max-w-[300px]"
      >
        {contractors.map((c, i) => {
          const pos = (i - activeIndex + n) % n;
          const pose = stackPose[Math.min(pos, 3)];
          return (
            <motion.div
              key={c.name}
              initial={false}
              animate={{ y: pose.y, scale: pose.scale, opacity: pose.opacity }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              style={{ zIndex: pose.zIndex }}
              className="absolute inset-x-0 top-10 overflow-hidden rounded-2xl bg-white ring-1 ring-[#e7eaf4] shadow-[0_24px_48px_-24px_rgba(101,129,234,0.45)]"
            >
              <div className="flex items-center gap-2 border-b border-[#f1f3f9] px-4 py-2.5">
                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-accent/10">
                  <c.Icon className="h-3.5 w-3.5 text-accent" strokeWidth={1.8} />
                </span>
                <span className="truncate text-[11.5px] font-semibold text-dark-teal">
                  {c.name}
                </span>
              </div>
              {c.docs.map((doc) => {
                const s = statusStyles[doc.status];
                return (
                  <div
                    key={doc.type}
                    className="flex items-center gap-2 border-b border-[#f5f6fa] px-4 py-2 last:border-0"
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${s.iconTone}`}
                    >
                      <s.Icon className="h-3 w-3" strokeWidth={1.8} />
                    </span>
                    <span className="flex-1 truncate text-[10.5px] font-medium text-dark-teal">
                      {doc.type}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[8.5px] font-semibold ${s.chip}`}
                    >
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}

// ======================== 2 · ACCENT CARD — VISIBILIDAD ========================

function AccentVisibilityCard() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  const R = 36;
  const C = 2 * Math.PI * R;
  const SEMI = C / 2;
  const targetOffset = SEMI * (1 - 87 / 100);

  return (
    <div
      ref={ref}
      className="relative flex h-full min-h-[420px] flex-col overflow-hidden rounded-[22px] bg-linear-to-b from-[#8DA4F8] via-[#6581EA] to-[#5566D2] p-4 shadow-[0_40px_70px_-32px_rgba(101,129,234,0.55)]"
    >
      {/* Hairline top light + ambient bloom */}
      <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-linear-to-r from-transparent via-white/60 to-transparent" />
      <div className="pointer-events-none absolute -top-16 -right-12 h-52 w-52 rounded-full bg-white/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-[#67B4C1]/30 blur-3xl" />

      <div className="relative flex items-center gap-2.5 px-2 pt-1.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/30">
          <Gauge className="h-4 w-4 text-white" strokeWidth={1.8} />
        </span>
        <span className="text-[13.5px] font-semibold text-white">
          Visibilidad en tiempo real
        </span>
      </div>

      <div className="relative flex flex-1 flex-col items-center justify-center py-6">
        <div className="relative w-[150px]">
          <svg viewBox="0 0 90 55" className="h-auto w-full overflow-visible">
            <circle
              cx="45"
              cy="45"
              r={R}
              fill="none"
              stroke="rgba(255,255,255,0.25)"
              strokeWidth="6"
              strokeDasharray={`${SEMI} ${C}`}
              transform="rotate(180 45 45)"
              strokeLinecap="round"
            />
            <motion.circle
              cx="45"
              cy="45"
              r={R}
              fill="none"
              stroke="#ffffff"
              strokeWidth="6"
              strokeDasharray={`${SEMI} ${C}`}
              initial={{ strokeDashoffset: SEMI }}
              animate={inView ? { strokeDashoffset: targetOffset } : {}}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
              transform="rotate(180 45 45)"
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-end justify-center pb-0.5">
            <span className="text-[26px] font-bold leading-none tracking-[-0.02em] text-white">
              <CountUp target={87} inView={inView} />
              <span className="text-[15px]">%</span>
            </span>
          </div>
        </div>
        <span className="mt-1.5 text-[11px] font-medium text-white/70">
          Cumplimiento global
        </span>

        <div className="mt-5 flex gap-2">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white/15 px-3 py-1.5 text-[10.5px] font-semibold text-white ring-1 ring-white/25">
            <Users className="h-3 w-3" strokeWidth={2} />
            <CountUp target={118} inView={inView} /> activos
          </span>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white/15 px-3 py-1.5 text-[10.5px] font-semibold text-white ring-1 ring-white/25">
            <Clock className="h-3 w-3" strokeWidth={2} />
            <CountUp target={35} inView={inView} /> por vencer
          </span>
        </div>
      </div>

      <div className="relative rounded-[16px] bg-white/[0.14] p-4 ring-1 ring-white/25 backdrop-blur-sm">
        <h3 className="mb-1 text-[15px] font-semibold text-white">
          Tu operación, al día
        </h3>
        <p className="text-[12.5px] leading-[1.55] text-white/75">
          Semáforo por contratista y proyecto: las brechas se ven antes de que
          sean un riesgo.
        </p>
      </div>
    </div>
  );
}

// ======================== 3 · ALERTAS AUTOMÁTICAS ========================

const alertTones: Record<
  "warning" | "danger" | "success",
  { Icon: LucideIcon; iconTone: string; chip: string }
> = {
  warning: {
    Icon: Clock3,
    iconTone: "bg-amber-50 text-amber-600",
    chip: "bg-amber-50 text-amber-700",
  },
  danger: {
    Icon: ShieldX,
    iconTone: "bg-red-50 text-red-500",
    chip: "bg-red-50 text-red-600",
  },
  success: {
    Icon: ShieldCheck,
    iconTone: "bg-emerald-50 text-emerald-600",
    chip: "bg-emerald-50 text-emerald-700",
  },
};

const alertPool = [
  {
    contractor: "Servicios ProClean",
    doc: "Póliza de seguro",
    badge: "Vence en 3 días",
    tone: "warning" as const,
  },
  {
    contractor: "Transportes Rápidos",
    doc: "Licencia municipal",
    badge: "Vencido hoy",
    tone: "danger" as const,
  },
  {
    contractor: "Constructora Andes",
    doc: "Certificado F30-1",
    badge: "Renovado",
    tone: "success" as const,
  },
  {
    contractor: "Eléctrica del Sur",
    doc: "Certificado SEC",
    badge: "Vence mañana",
    tone: "warning" as const,
  },
  {
    contractor: "Aseo Industrial",
    doc: "Contrato servicio",
    badge: "Vencido",
    tone: "danger" as const,
  },
  {
    contractor: "Servicios ProClean",
    doc: "Reg. sanitario",
    badge: "Actualizado",
    tone: "success" as const,
  },
];

function AutoAlertsMockup() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [tick, setTick] = useState(2);
  const hovered = useRef(false);

  useEffect(() => {
    if (!inView) return;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    const timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        if (hovered.current) return;
        setTick((t) => t + 1);
      }, 3000);
    }, 1500);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId !== undefined) clearInterval(intervalId);
    };
  }, [inView]);

  const visibleAlerts = [0, 1, 2].map((offset) => {
    const t = tick - offset;
    const poolIndex =
      ((t % alertPool.length) + alertPool.length) % alertPool.length;
    return { ...alertPool[poolIndex], key: t, isNew: offset === 0 && tick > 2 };
  });

  return (
    <div
      ref={ref}
      className="flex h-full flex-col justify-center gap-2 overflow-hidden px-5 [mask-image:linear-gradient(to_bottom,transparent_0%,black_12%,black_88%,transparent_100%)]"
      onMouseEnter={() => {
        hovered.current = true;
      }}
      onMouseLeave={() => {
        hovered.current = false;
      }}
    >
      <AnimatePresence initial={false} mode="popLayout">
        {visibleAlerts.map((alert) => {
          const t = alertTones[alert.tone];
          return (
            <motion.div
              key={alert.key}
              layout
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="flex items-start gap-2.5 rounded-xl bg-white px-3 py-2.5 ring-1 ring-[#e9ecf5] shadow-[0_10px_24px_-14px_rgba(101,129,234,0.4)]"
            >
              <div
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${t.iconTone}`}
              >
                <t.Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-0.5 flex items-center gap-1.5">
                  <span className="truncate text-[10.5px] font-semibold text-dark-teal">
                    {alert.contractor}
                  </span>
                  {alert.isNew && (
                    <span className="relative flex h-1.5 w-1.5 shrink-0">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-50" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[9.5px] text-dark-teal/50">
                    {alert.doc}
                  </span>
                  <span
                    className={`ml-auto inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[8.5px] font-semibold ${t.chip}`}
                  >
                    {alert.badge}
                  </span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

// ======================== 4 · CUMPLIMIENTO POR PROYECTO ========================

function ProjectComplianceMockup() {
  const [activeTab, setActiveTab] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const hovered = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!inView) return;
    const timeoutId = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        if (hovered.current) return;
        setActiveTab((prev) => (prev + 1) % 3);
      }, 3500);
    }, 2000);
    return () => {
      clearTimeout(timeoutId);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [inView]);

  const handleTabClick = (i: number) => {
    setActiveTab(i);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (hovered.current) return;
      setActiveTab((prev) => (prev + 1) % 3);
    }, 3500);
  };

  const tabData = [
    [
      { name: "Constructora Andes", fill: 92 },
      { name: "Servicios ProClean", fill: 58 },
      { name: "Transportes Rápidos", fill: 81 },
    ],
    [
      { name: "Eléctrica del Sur", fill: 41 },
      { name: "Aseo Industrial", fill: 97 },
      { name: "Constructora Andes", fill: 15 },
    ],
    [
      { name: "Transportes Rápidos", fill: 74 },
      { name: "Servicios ProClean", fill: 33 },
      { name: "Eléctrica del Sur", fill: 89 },
    ],
  ];

  return (
    <div
      ref={ref}
      className="flex h-full flex-col items-center justify-center gap-3 px-5"
      onMouseEnter={() => {
        hovered.current = true;
      }}
      onMouseLeave={() => {
        hovered.current = false;
      }}
    >
      <div className="inline-flex rounded-full bg-[#edf0f8] p-1">
        {["Proyecto A", "Proyecto B", "Proyecto C"].map((name, i) => (
          <button
            key={i}
            type="button"
            onClick={() => handleTabClick(i)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[10px] font-semibold transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
              i === activeTab
                ? "bg-white text-accent shadow-[0_4px_12px_-4px_rgba(101,129,234,0.4)]"
                : "text-dark-teal/45 hover:text-dark-teal/70"
            }`}
          >
            {name}
          </button>
        ))}
      </div>

      <div className="w-full rounded-2xl bg-white p-4 ring-1 ring-[#e9ecf5] shadow-[0_16px_36px_-20px_rgba(101,129,234,0.4)]">
        <div className="flex flex-col gap-3">
          {tabData[activeTab].map((bar, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <span className="w-[84px] shrink-0 truncate text-[9.5px] font-medium text-dark-teal/60">
                {bar.name}
              </span>
              <div className="h-2 flex-1 rounded-full bg-[#edf0f8]">
                <motion.div
                  initial={{ width: 0 }}
                  animate={inView ? { width: `${bar.fill}%` } : { width: 0 }}
                  transition={{ type: "spring", stiffness: 50, damping: 20 }}
                  className="h-full rounded-full transition-[background] duration-500"
                  style={{
                    background:
                      bar.fill < 20
                        ? "linear-gradient(to right, #F87171, #EF4444)"
                        : bar.fill >= 75
                          ? "linear-gradient(to right, #8DA4F8, #6581EA)"
                          : "linear-gradient(to right, #FBBF24, #F59E0B)",
                  }}
                />
              </div>
              <span
                className={`w-[28px] shrink-0 text-right text-[10px] font-bold transition-colors duration-500 ${
                  bar.fill < 20
                    ? "text-red-500"
                    : bar.fill >= 75
                      ? "text-accent"
                      : "text-amber-500"
                }`}
              >
                {bar.fill}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ======================== 5 · REPORTES ACCIONABLES ========================

function ActionableReportsMockup() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  const rows = [
    { contractor: "Constructora Andes", type: "F30-1", status: "vigente" as const },
    { contractor: "Servicios ProClean", type: "Póliza", status: "por_vencer" as const },
    { contractor: "Transp. Rápidos", type: "Licencia", status: "vencido" as const },
  ];

  return (
    <div ref={ref} className="relative flex h-full items-center justify-center px-6">
      {/* Floating export chip, overlapping the table */}
      <motion.div
        initial={{ opacity: 0, y: -8, rotate: 0 }}
        animate={inView ? { opacity: 1, y: 0, rotate: 3 } : {}}
        transition={{ duration: 0.6, ease: EASE, delay: 0.5 }}
        className="absolute right-8 top-6 z-10 flex items-center gap-1.5 rounded-xl bg-white px-3 py-1.5 ring-1 ring-[#e7eaf4] shadow-[0_16px_32px_-12px_rgba(101,129,234,0.5)]"
      >
        <Download className="h-3.5 w-3.5 text-accent" strokeWidth={2} />
        <span className="text-[10px] font-semibold text-accent">
          Exportar PDF
        </span>
      </motion.div>

      <div className="mt-4 w-full max-w-[300px] overflow-hidden rounded-2xl bg-white ring-1 ring-[#e7eaf4] shadow-[0_24px_48px_-24px_rgba(101,129,234,0.45)]">
        <div className="flex items-center gap-3 border-b border-[#f1f3f9] bg-[#fafbfe] px-3.5 py-2">
          <span className="flex-1 text-[8.5px] font-semibold uppercase tracking-wider text-dark-teal/40">
            Contratista
          </span>
          <span className="w-[52px] text-[8.5px] font-semibold uppercase tracking-wider text-dark-teal/40">
            Tipo
          </span>
          <span className="w-[64px] text-right text-[8.5px] font-semibold uppercase tracking-wider text-dark-teal/40">
            Estado
          </span>
        </div>
        {rows.map((row, i) => {
          const s = statusStyles[row.status];
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: 12 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 24,
                delay: 0.15 + i * 0.08,
              }}
              className="flex items-center gap-3 border-b border-[#f5f6fa] px-3.5 py-2 last:border-0"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${s.iconTone}`}
                >
                  <s.Icon className="h-3 w-3" strokeWidth={1.8} />
                </span>
                <span className="truncate text-[10px] font-medium text-dark-teal">
                  {row.contractor}
                </span>
              </div>
              <span className="w-[52px] text-[9.5px] text-dark-teal/50">
                {row.type}
              </span>
              <div className="flex w-[64px] justify-end">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[8.5px] font-semibold ${s.chip}`}
                >
                  {s.label}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ======================== 6 · EVIDENCIA PARA AUDITORÍAS ========================

const auditEvents = [
  {
    Icon: CircleCheck,
    tone: "bg-emerald-50 text-emerald-600",
    label: "Documento aprobado",
    meta: "Póliza · Constructora Andes",
    time: "hace 2 min",
  },
  {
    Icon: Send,
    tone: "bg-accent/10 text-accent",
    label: "Recordatorio enviado",
    meta: "F30-1 · Servicios ProClean",
    time: "hace 1 h",
  },
  {
    Icon: FileCheck2,
    tone: "bg-[#edf0f8] text-dark-teal/60",
    label: "Evidencia exportada",
    meta: "PDF · Proyecto Norte",
    time: "ayer",
  },
];

function AuditTrailMockup() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  return (
    <div ref={ref} className="flex h-full flex-col justify-center px-6">
      <div className="relative flex flex-col gap-2.5">
        {/* Connecting line */}
        <div className="absolute bottom-6 left-[26px] top-6 w-px bg-[#e5e8f2]" />
        {auditEvents.map((ev, i) => (
          <motion.div
            key={ev.label}
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, ease: EASE, delay: 0.15 + i * 0.12 }}
            className="relative flex items-center gap-2.5 rounded-xl bg-white px-3 py-2.5 ring-1 ring-[#e9ecf5] shadow-[0_10px_24px_-14px_rgba(101,129,234,0.4)]"
          >
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${ev.tone}`}
            >
              <ev.Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[10.5px] font-semibold text-dark-teal">
                {ev.label}
              </p>
              <p className="truncate text-[9.5px] text-dark-teal/50">{ev.meta}</p>
            </div>
            <span className="shrink-0 text-[9px] font-medium text-dark-teal/35">
              {ev.time}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ======================== CARD SHELL ========================

function BentoCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="group flex h-full flex-col rounded-[22px] bg-white p-2.5 ring-1 ring-[#e8ebf4] transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] hover:ring-accent/30">
      {/* Inner visual tray (double-bezel) */}
      <div className="relative min-h-[228px] flex-1 overflow-hidden rounded-[16px] bg-[#f7f8fc] ring-1 ring-[#eef0f7]">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-44 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/[0.09] blur-3xl" />
        {children}
      </div>
      <div className="px-3.5 pb-3 pt-4">
        <h3 className="mb-1 text-[15.5px] font-semibold tracking-tight text-dark-teal">
          {title}
        </h3>
        <p className="text-[13px] leading-[1.55] text-dark-teal/55">
          {description}
        </p>
      </div>
    </div>
  );
}

// ======================== SECTION ========================

export default function Features() {
  return (
    <section
      id="plataforma"
      aria-label="Funcionalidades de la plataforma Verify"
      className="relative overflow-hidden px-6 pb-16 pt-16 sm:pb-20 sm:pt-20"
    >
      {/* Ambient brand glows behind the bento */}
      <div
        className="pointer-events-none absolute left-1/4 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2"
        style={{
          background:
            "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(101,129,234,0.07) 0%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute right-1/4 top-1/3 h-[500px] w-[500px] -translate-y-1/2 translate-x-1/2"
        style={{
          background:
            "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(103,180,193,0.06) 0%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute bottom-0 left-1/2 h-[400px] w-[700px] -translate-x-1/2 translate-y-1/3"
        style={{
          background:
            "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(101,129,234,0.05) 0%, transparent 70%)",
        }}
      />

      <div className="relative">
        <div className="mx-auto mb-12 max-w-[60rem] text-center">
          <ScrollReveal>
            <span className="mb-1 inline-block rounded-full px-3.5 py-1 text-sm font-semibold text-accent sm:text-base">
              Características
            </span>
          </ScrollReveal>
          <ScrollReveal delay={0.08}>
            <h2 className="flex flex-col text-balance text-[2rem] font-medium leading-[1.1] tracking-[-0.02em] text-dark-teal sm:text-[36px] sm:leading-[1.05] lg:text-5xl">
              <span className="bg-linear-to-r from-[#4A66D9] to-[#3F8E9D] bg-clip-text text-transparent">
                Menos supuestos.
              </span>
              <span>Más certezas.</span>
            </h2>
          </ScrollReveal>
        </div>

        <div className="mx-auto grid max-w-[68rem] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <ScrollReveal className="h-full">
            <BentoCard
              title="Control documental centralizado"
              description="Toda la documentación de contratistas y trabajadores con estados claros y trazabilidad."
            >
              <DocumentStackMockup />
            </BentoCard>
          </ScrollReveal>

          <ScrollReveal delay={0.08} className="h-full">
            <AccentVisibilityCard />
          </ScrollReveal>

          <ScrollReveal delay={0.16} className="h-full">
            <BentoCard
              title="Alertas automáticas"
              description="Avisos proactivos antes de que un vencimiento se convierta en un riesgo."
            >
              <AutoAlertsMockup />
            </BentoCard>
          </ScrollReveal>

          <ScrollReveal className="h-full">
            <BentoCard
              title="Cumplimiento por proyecto"
              description="Requisitos segmentados por proyecto u operación, adaptados a cada contexto."
            >
              <ProjectComplianceMockup />
            </BentoCard>
          </ScrollReveal>

          <ScrollReveal delay={0.08} className="h-full">
            <BentoCard
              title="Reportes accionables"
              description="Exporta información clave para auditorías y fiscalizaciones en un clic."
            >
              <ActionableReportsMockup />
            </BentoCard>
          </ScrollReveal>

          <ScrollReveal delay={0.16} className="h-full">
            <BentoCard
              title="Evidencia para auditorías"
              description="Historial de actividad y respaldos en PDF listos para cualquier fiscalización."
            >
              <AuditTrailMockup />
            </BentoCard>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}

